import { useState } from "react";
import { Clock, Calendar, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ParticipantList from "./ParticipantList";

interface PendingStateCardProps {
  title: string;
  message?: string;
  icon?: 'clock' | 'calendar' | 'users';
  type?: 'match' | 'standings' | 'bracket' | 'general';
  className?: string;
  showParticipants?: boolean;
  gameType?: 'singles' | 'doubles';
  eventType?: 'tournament' | 'league';
  eventTitle?: string;
}

const PendingStateCard = ({ 
  title, 
  message = "Por confirmar", 
  icon = 'clock',
  type = 'general',
  className = "",
  showParticipants = false,
  gameType = 'doubles',
  eventType = 'tournament',
  eventTitle = ''
}: PendingStateCardProps) => {
  const [showParticipantList, setShowParticipantList] = useState(false);
  const getIcon = () => {
    switch (icon) {
      case 'calendar':
        return <Calendar className="h-6 w-6 text-slate-400" />;
      case 'users':
        return <Users className="h-6 w-6 text-slate-400" />;
      default:
        return <Clock className="h-6 w-6 text-slate-400" />;
    }
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'match':
        return "bg-gradient-to-br from-slate-50 to-primary/5 border-slate-200";
      case 'standings':
        return "bg-gradient-to-br from-slate-50 to-green-50 border-slate-200";
      case 'bracket':
        return "bg-gradient-to-br from-slate-50 to-purple-50 border-slate-200";
      default:
        return "bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200";
    }
  };

  if (showParticipantList) {
    return (
      <div className="space-y-4">
        <Button 
          variant="outline" 
          onClick={() => setShowParticipantList(false)}
          className="mb-4"
        >
          ← Volver
        </Button>
        <ParticipantList 
          gameType={gameType}
          eventType={eventType}
          eventTitle={eventTitle}
        />
      </div>
    );
  }

  return (
    <Card className={`${getTypeStyles()} ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-slate-700">
          {getIcon()}
          <span className="text-lg">{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <Badge 
            variant="outline" 
            className="border-slate-300 text-slate-600 bg-white/50 backdrop-blur-sm px-4 py-2 text-sm"
          >
            {message}
          </Badge>
          <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
            La información se actualizará automáticamente cuando inicie el evento
          </p>
          {showParticipants && (
            <Button 
              variant="outline"
              onClick={() => setShowParticipantList(true)}
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              <Users className="h-4 w-4 mr-2" />
              Ver participantes
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingStateCard;